import {
  Container,
  Flex,
  Paper,
  Text,
  Badge,
  Tabs,
  Anchor,
  Title,
  Box,
  Button,
  Divider,
  SimpleGrid,
  Stack,
} from "@mantine/core";
import {
  IconBuilding,
  IconMapPin,
  IconPhone,
  IconMail,
  IconRosetteDiscountCheckFilled,
  IconWorld,
  IconBrandLinkedin,
  IconExternalLink,
  IconArrowLeft,
  IconClipboardList,
  IconEye,
} from "@tabler/icons-react";
import MatchedProfileTab from "@/pages/profile/MatchedPrfofielTab";
import StatusBadge from "@/pages/profile-verification/StatusBadge";
import DocumentsTab from "@/components/shared/AnonymousCompanyProfile/DocumentsTab";
import ImagesTabContent from "@/components/profile/ImagesTabContent";
import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useUserContext } from "@/context/UserContext";
import { getFileUrl } from "@/shared/utils/useFileUrl";

export type QuestionnaireCta = {
  type: "start" | "view";
  onClick: () => void;
};

interface MatchedProfileViewProps {
  company?: any;
  containerSize?: "fluid";
  isOwnProfile?: boolean;
  /** Collaboration workspace: questionnaire action inside the header card */
  questionnaireCta?: QuestionnaireCta | null;
}

export default function MatchedProfileView({
  company: propCompany,
  containerSize = "fluid",
  isOwnProfile = false,
  questionnaireCta = null,
}: MatchedProfileViewProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails, hasProfile } = useUserContext();

  const [company, setCompany] = useState<any>(propCompany || null);
  const [collaboration, setCollaboration] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<string | null>("profile");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  // Check if we're on the collaboration profile route
  const isCollaborationProfile =
    location.pathname.includes("/collaborations/") && location.pathname.includes("/profile");

  useEffect(() => {
    // Only fetch if we don't have company data from props and we're on collaboration profile route
    if (hasProfile === null) return;
    if (!propCompany && isCollaborationProfile && id) {
      const fetchCompanyData = async () => {
        try {
          const collabRecord = await trpc.collaboration.getById.query({ matchId: id });
          setCollaboration(collabRecord);

          const myCompanyId = userDetails?.company?.id;
          const otherCompanyId =
            collabRecord.requesterCompanyId === myCompanyId
              ? collabRecord.receiverCompanyId
              : collabRecord.requesterCompanyId;

          const companyData = await trpc.company.getById.query({ companyId: otherCompanyId });
          setCompany(companyData);
        } catch (err) {
          console.error("Error fetching company:", err);
          handleTrpcError(err);
        }
      };

      fetchCompanyData();
    }
  }, [id, propCompany, isCollaborationProfile, userDetails?.company?.id, hasProfile]);

  // Fetch logo URL from S3 when company changes
  useEffect(() => {
    if (company?.logo?.id) {
      getFileUrl(company.logo.id).then((url) => {
        setLogoUrl(url);
      });
    }
  }, [company]);

  // Safety check - if no company data, show error
  if (!company) {
    return (
      <Container size={containerSize} py="xl">
        <Text>No company data available</Text>
      </Container>
    );
  }

  return (
    <Box>
      {/* Back Button - Only show on collaboration profile route */}
      {isCollaborationProfile && (
        <Box py="sm" px="xl">
          <Button
            variant="subtle"
            color="gray"
            leftSection={<IconArrowLeft size={16} />}
            size="sm"
            onClick={() => navigate(`/collaborations/${id}`)}
          >
            Back to Workspace
          </Button>
        </Box>
      )}

      <Container size={containerSize} pb="xs">
        {/* Company Header - Shows REAL data */}
        <Paper p="xl" radius="lg" mb="xl" style={{ border: "1px solid #04305c", backgroundColor: "#ebf0ff" }}>
          <Flex gap="md" align="flex-start" justify="space-between">
            <Flex
              w={{ base: 48, sm: 80 }}
              h={{ base: 48, sm: 80 }}
              style={{
                borderRadius: 8,
                border: "1px solid #04305c",
                backgroundColor: "#F9FAFB",
                flexShrink: 0,
                overflow: "hidden",
              }}
              align="center"
              justify="center"
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Company Logo"
                  style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }}
                />
              ) : (
                <IconBuilding size={32} color="#6B7280" />
              )}
            </Flex>

            <Flex direction="column" style={{ flex: 1 }} gap={6}>
              {/* Row 1: Company Name + Verified Badge */}
              <Flex align="center" gap="sm">
                <Title order={2} fw={400} fz={22}>
                  {company.legalName || "Company Name"}
                </Title>
                {company.status === "VERIFIED" && (
                  <IconRosetteDiscountCheckFilled size={20} style={{ color: "var(--mantine-color-primary-6)" }} />
                )}
              </Flex>

              {/* Row 2: GTID Badge */}
              {company.gtid && (
                <Badge
                  variant="light"
                  color="#EEF2F7"
                  size="xl"
                  radius="sm"
                  style={{
                    width: "fit-content",
                    backgroundColor: "#EEF2F7",
                    color: "#374151",
                  }}
                >
                  {company.gtid}
                </Badge>
              )}

              {/* Row 3: Location, Phone, Email */}
              <Flex gap="md" wrap="wrap" align="center">
                <Flex align="center" gap={6}>
                  <IconMapPin size={16} color="#6B7280" />
                  <Text size="sm" c="slate.7">
                    {company.headquartersCity && company.headquartersCountry
                      ? `${company.headquartersCity}, ${company.headquartersCountry}`
                      : company.headquartersCountry || "N/A"}
                  </Text>
                </Flex>
                <Flex align="center" gap={6}>
                  <IconPhone size={16} color="#6B7280" />
                  <Text size="sm" c="slate.7">
                    {company.phoneCountryCode && company.phoneNumber
                      ? `${company.phoneCountryCode} ${company.phoneNumber}`
                      : "N/A"}
                  </Text>
                </Flex>
                <Flex align="center" gap={6}>
                  <IconMail size={16} color="#6B7280" />
                  <Text size="sm" c="slate.7" style={{ wordBreak: "break-all" }}>
                    {company.email || "N/A"}
                  </Text>
                </Flex>
              </Flex>

              {/* Row 4: Website and LinkedIn */}
              <Flex gap="md" wrap="wrap" align="center">
                {company.website && (
                  <Anchor
                    href={company.website}
                    target="_blank"
                    td="none"
                    styles={{
                      root: {
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      },
                    }}
                  >
                    <Flex align="center" gap={6}>
                      <IconWorld size={16} color="var(--mantine-color-primary-6)" />
                      <Text size="sm" c="primary.6">
                        Website
                      </Text>
                      <IconExternalLink size={14} color="var(--mantine-color-primary-6)" />
                    </Flex>
                  </Anchor>
                )}
                {company.linkedinUrl && (
                  <Anchor
                    href={company.linkedinUrl}
                    target="_blank"
                    td="none"
                    styles={{
                      root: {
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      },
                    }}
                  >
                    <Flex align="center" gap={6}>
                      <IconBrandLinkedin size={16} color="var(--mantine-color-primary-6)" />
                      <Text size="sm" c="primary.6">
                        LinkedIn
                      </Text>
                    </Flex>
                  </Anchor>
                )}
              </Flex>
            </Flex>
            {(questionnaireCta ||
              (isOwnProfile && ["UNDER_REVIEW", "VERIFIED", "SYSTEM_VERIFIED"].includes(company.status))) && (
              <Flex direction="column" align="flex-end" gap="sm" style={{ flexShrink: 0 }}>
                {questionnaireCta && (
                  <Button
                    variant="filled"
                    size="sm"
                    w={{ base: "100%", sm: "auto" }}
                    styles={{
                      root: {
                        backgroundColor: "#ffc300",
                        color: "#04305c",
                        border: "1px solid #ffc300",
                        "&:hover": {
                          backgroundColor: "#e6b000",
                          borderColor: "#e6b000",
                        },
                      },
                    }}
                    leftSection={
                      questionnaireCta.type === "start" ? (
                        <IconClipboardList size={18} color="#04305c" />
                      ) : (
                        <IconEye size={18} color="#04305c" />
                      )
                    }
                    onClick={questionnaireCta.onClick}
                  >
                    {questionnaireCta.type === "start" ? "Start questionnaire" : "View questionnaire"}
                  </Button>
                )}
                {isOwnProfile && ["UNDER_REVIEW", "VERIFIED", "SYSTEM_VERIFIED"].includes(company.status) && (
                  <StatusBadge status={company.status} />
                )}
              </Flex>
            )}
          </Flex>
        </Paper>

        {/* Tabs Section */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List
            styles={{
              list: {
                flexWrap: "wrap",
                alignItems: "flex-end",
                borderBottom: "2px solid #04305c",
                boxShadow: "none",
                gap: 0,
              },
            }}
          >
            <Tabs.Tab
              value="profile"
              px={{ base: "md", sm: "lg" }}
              py="xs"
              fz={{ base: "xs", sm: "sm" }}
              style={{
                backgroundColor: activeTab === "profile" ? "#04305c" : "#ebf0ff",
                color: activeTab === "profile" ? "white" : "var(--mantine-color-slate-6)",
                borderRadius: "var(--mantine-radius-md) var(--mantine-radius-md) 0 0",
                border: activeTab === "profile" ? "1px solid #04305c" : "1px solid #E5E7EB",
                borderBottom: "none",
                whiteSpace: "nowrap",
                fontWeight: activeTab === "profile" ? 500 : 400,
                cursor: "pointer",
                minHeight: 44,
              }}
              rightSection={
                <Box
                  w={28}
                  h={28}
                  miw={28}
                  mih={28}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                />
              }
            >
              Company Profile
            </Tabs.Tab>
            <Tabs.Tab
              value="images"
              px={{ base: "md", sm: "lg" }}
              py="xs"
              fz={{ base: "xs", sm: "sm" }}
              style={{
                backgroundColor: activeTab === "images" ? "#04305c" : "#ebf0ff",
                color: activeTab === "images" ? "white" : "var(--mantine-color-slate-6)",
                borderRadius: "var(--mantine-radius-md) var(--mantine-radius-md) 0 0",
                border: activeTab === "images" ? "1px solid #04305c" : "1px solid #E5E7EB",
                borderBottom: "none",
                whiteSpace: "nowrap",
                fontWeight: activeTab === "images" ? 500 : 400,
                cursor: "pointer",
                minHeight: 44,
              }}
              rightSection={
                <Box
                  w={28}
                  h={28}
                  miw={28}
                  mih={28}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                />
              }
            >
              Images and Documents
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="profile" pt="xl">
            <MatchedProfileTab company={company} collaboration={collaboration} />
          </Tabs.Panel>

          <Tabs.Panel value="images" pt="xl">
            {activeTab === "images" && (
              <ImagesTabContent
                company={company}
                activeTab={activeTab}
                refreshCompany={async () => {}}
                isViewMode={true}
              />
            )}
            <Divider my="xl" size={2} color="#04305c" styles={{ root: { boxShadow: "none" } }} />
            <DocumentsTab company={company} isMatchedView={true} />
          </Tabs.Panel>
        </Tabs>

        {/* About Verified Listings */}
        <Paper
          p={0}
          radius="lg"
          mt="xl"
          style={{
            overflow: "hidden",
            backgroundColor: "#ebf0ff",
            boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Flex
            align="center"
            gap="sm"
            px={{ base: "md", sm: "xl" }}
            py={{ base: "md", sm: "lg" }}
            style={{ backgroundColor: "#04305c" }}
          >
            <IconBuilding size={22} color="#ffc300" stroke={1.5} />
            <Text fw={700} fz="md" c="#ffc300" style={{ letterSpacing: 0.2 }}>
              About Verified Listings
            </Text>
          </Flex>
          <Box p={{ base: "lg", sm: "xl" }}>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={{ base: "lg", sm: "xl" }} verticalSpacing="md">
              <Stack gap="md">
                <Flex align="flex-start" gap="sm">
                  <Box mt={6} w={6} h={6} style={{ flexShrink: 0, backgroundColor: "#04305c" }} />
                  <Text size="sm" fw={700} lh={1.55} c="#04305c">
                    Full company details are visible after mutual interest is confirmed
                  </Text>
                </Flex>
                <Flex align="flex-start" gap="sm">
                  <Box mt={6} w={6} h={6} style={{ flexShrink: 0, backgroundColor: "#04305c" }} />
                  <Text size="sm" fw={700} lh={1.55} c="#04305c">
                    Verification badge confirms company authenticity
                  </Text>
                </Flex>
              </Stack>
              <Stack gap="md">
                <Flex align="flex-start" gap="sm">
                  <Box mt={6} w={6} h={6} style={{ flexShrink: 0, backgroundColor: "#04305c" }} />
                  <Text size="sm" fw={700} lh={1.55} c="#04305c">
                    Contact information becomes accessible to matched companies
                  </Text>
                </Flex>
                <Flex align="flex-start" gap="sm">
                  <Box mt={6} w={6} h={6} style={{ flexShrink: 0, backgroundColor: "#04305c" }} />
                  <Text size="sm" fw={700} lh={1.55} c="#04305c">
                    Both parties can initiate collaboration discussions
                  </Text>
                </Flex>
              </Stack>
            </SimpleGrid>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
